import React, { useEffect, useMemo, useCallback, useState, useContext } from 'react';
import BackendConnector from "../middleware/BackendConnector";
import { Box, CircularProgress, Button, Typography } from "@mui/material";
import { socket } from '../libs/webRTC';


const UserContext = React.createContext();

function UserProvider(props) {
    const [authUnavailable, setAuthUnavailable] = useState(false);
    const [authAttempt, setAuthAttempt] = useState(0);
    const [user, setUser] = useState("loading");
    useEffect(() => {
        if (user && user !== 'loading') socket.connect();
        else socket.disconnect();
    }, [user]);
    useEffect(() => {
        const expire = () => setUser(null);
        window.addEventListener('authentication-expired', expire);
        return () => window.removeEventListener('authentication-expired', expire);
    }, []);

    useEffect(() => {
        let active = true;
        setAuthUnavailable(false);
        BackendConnector.getCurrentUser()
            .then((userData) => {
                if (active) setUser(userData);
            })
            .catch(() => {
                if (active) setAuthUnavailable(true);
            });
        return () => { active = false; };
    }, [authAttempt]);

    const login = useCallback(async (params) => {
        const result = await BackendConnector.login(params);
        setUser(result.status ? result.user : null);
        return result.status;
    }, []);

    const logout = useCallback(async () => {
        try {
            await BackendConnector.logout();
            setUser(null);
        } catch {
            setAuthUnavailable(true);
        }
    }, [setUser]);

    const get_routes = useCallback(() => {
        if (user && user.routes) {
            return user.routes
        }
        else {
            return []
        }
    }, [user]);

    const get_name = useCallback(() => {
        if (user && user.name) {
            return user.name
        }
        else {
            return null
        }
    }, [user]);

    const get_group = useCallback(() => {
        if (user && user.group) {
            return user.group
        }
        else {
            return ""
        }
    }, [user]);

    const get_login = useCallback(() => {
        if (user && user.login) {
            return user.login
        }
        else {
            return null
        }
    }, [user]);

    const isAdmin = useCallback(() => {
        if (user) {
            return user.isAdmin
        }
        else {
            return null
        }
    }, [user]);

    const contextValue = useMemo(() => ({
        user,
        login,
        logout,
        get_routes,
        get_name,
        get_login,
        get_group,
        isAdmin
    }), [user, login, logout, get_routes, get_name, get_login, get_group, isAdmin]);

    if (authUnavailable) {
        return <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography>Сервер авторизации временно недоступен.</Typography>
            <Button onClick={() => setAuthAttempt(value => value + 1)}>Повторить</Button>
        </Box>;
    }

    if (user === "loading") {
        return <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size="15rem" />
        </Box>;
    }

    return (
        <UserContext.Provider value={contextValue} {...props} />
    );
};

function useUser() {
    const context = useContext(UserContext)
    if (context === undefined) {
        throw new Error("useUser() must be used within a UserProvider");
    }
    return context
};

export { UserProvider, useUser };
